import { Button, Dropdown, IconButton, Modal } from "@alm/flow-ds/components";
import axios from "axios";
import CardColumn from "components/CardColumn";
import api from "lib/api";
import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { exportFile } from "utils/exportFile";
import { Workflow } from "./Workflows";

interface Props extends Workflow {
	deleteWorkflow: (id: string) => void;
}

export default function WorkflowCard({
	id,
	name,
	description,
	deleteWorkflow,
}: Props) {
	const { t } = useTranslation();

	const [isWarningOpen, setIsWarningOpen] = useState(false);

	// Warning type to display
	const [warning, setWarning] = useState<string | undefined>();

	// Activties using the workflow
	const [inUse, setInUse] = useState([]);

	// Downloads the XML from workflow service
	const handleDownload = useCallback(async () => {
		try {
			const { data } = await api.workflow.downloadWorkflowDefinition(id);
			exportFile(id, data, "xml");
		} catch (e) {
			console.error(e);
		}
	}, [id]);

	const handlePreDelete = useCallback(async () => {
		try {
			// Check if there are active instances
			const res = await api.workflow.checkIsWorkflowDefinitionInUse(id);
			// If tthere are no active instances, we can open the delete modal
			if (res.status === 200) {
				setWarning("confirmDelete");
				setIsWarningOpen(true);
			}
		} catch (error) {
			console.error(error);
			if (
				error &&
				axios.isAxiosError(error) &&
				error.response?.status === 400
			) {
				// Get the active instances and store it in inUse so we can display
				// the ids in the modal
				setInUse(error?.response?.data["@error"]?.["@messages"]);
				setWarning("workflowCantDelete");
				setIsWarningOpen(true);
			}
		}
	}, [id]);

	const handleDelete = useCallback(async () => {
		// Delete the workflow and then close the warning modal
		deleteWorkflow(id);
		setIsWarningOpen(false);
	}, [deleteWorkflow, id]);

	return (
		<>
			<CardColumn
				className="workflow-card__id d-none d-sm-none d-md-block text-truncate"
				width="sm">
				{id}
			</CardColumn>
			<CardColumn
				className="workflow-card__title text-truncate"
				width="md">
				{name}
			</CardColumn>
			<CardColumn
				className="workflow-card__description text-truncate"
				width="xl">
				{description}
			</CardColumn>
			<CardColumn className="workflow-card__menu">
				<Dropdown
					id={`workflow-card__dropdown__${id}`}
					menuBtn={
						<IconButton
							className="btn-basic--dark"
							icon="ellipsis-v"
							label={t("main.labels.menu")}
						/>
					}
					align="right">
					<Button
						name="download"
						label={t("common.labels.download")}
						className="dropdown-item btn-basic--default"
						onClick={handleDownload}
					/>
					<Button
						name="delete"
						label={t("common.labels.delete")}
						className="dropdown-item btn-basic--default"
						onClick={handlePreDelete}
					/>
				</Dropdown>
			</CardColumn>
			{isWarningOpen && (
				<Modal
					title={t(`alerts.${warning}.title`, { workflow: id })}
					isOpen={isWarningOpen}
					close={() => setIsWarningOpen(false)}
					footer={
						warning === "confirmDelete" ? (
							<>
								<Button
									className="btn-outline-primary"
									onClick={() => setIsWarningOpen(false)}
									label={t("common.labels.cancel")}
								/>
								<Button
									className="btn-danger"
									onClick={handleDelete}
									label={t("common.labels.delete")}
								/>
							</>
						) : (
							<Button
								className="btn-primary"
								onClick={() => setIsWarningOpen(false)}
								label={t("common.labels.ok")}
							/>
						)
					}>
					<Trans
						i18nKey={t(`alerts.${warning}.description`)}
						components={[
							<div className="my-4 workflow-modal-list">
								{inUse?.map(item => (
									<div key={item}>{item}</div>
								))}
							</div>,
						]}
					/>
				</Modal>
			)}
		</>
	);
}
